import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { Check, CircleAlert } from 'lucide-react'
import { Input, Label } from '@agion/ui'

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'> {
  /** Input id; the label and the message are wired to it. */
  id: string
  label: string
  /** Error message: red border, focus-gated red ring, inline message, aria-invalid. */
  error?: string
  /** Valid state: trailing check, green border and ring only while focused. */
  valid?: boolean
  /** Message under a valid field, e.g. the password strength word. */
  successMessage?: string
  /** Muted hint shown when the field has neither an error nor a success message. */
  hint?: string
  /** Leading icon inside the field. */
  leadingIcon?: ReactNode
  /** Control at the right end of the label row, e.g. Show / Hide. */
  labelAction?: ReactNode
  /** Read-only verified value on the muted fill; pair with `trailing`. */
  locked?: boolean
  /** Replaces the default valid check, e.g. the Verified marker. */
  trailing?: ReactNode
}

/**
 * Labeled auth input with the kit's validation states. Presentation only: each page
 * owns its validation rules and passes the result in.
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { id, label, error, valid, successMessage, hint, leadingIcon, labelAction, locked, trailing, ...inputProps },
  ref,
) {
  const messageId = useId()
  const state = error ? 'error' : valid ? 'valid' : 'neutral'
  const trailingNode = trailing ?? (valid && !error ? <Check size={16} aria-hidden="true" /> : null)

  let message: ReactNode = null
  if (error) {
    message = (
      <p id={messageId} className="lp-field-msg lp-field-msg-error">
        <CircleAlert size={14} strokeWidth={2.5} aria-hidden="true" />
        {error}
      </p>
    )
  } else if (valid && successMessage) {
    message = (
      <p id={messageId} className="lp-field-msg lp-field-msg-success">
        <Check size={14} strokeWidth={2.5} aria-hidden="true" />
        {successMessage}
      </p>
    )
  } else if (hint) {
    message = (
      <p id={messageId} className="lp-field-msg lp-field-msg-hint">
        {hint}
      </p>
    )
  }

  return (
    <div className="lp-field">
      <div className="lp-field-labelrow">
        <Label htmlFor={id} className="lp-field-label">
          {label}
        </Label>
        {labelAction}
      </div>
      <div
        className="lp-field-wrap"
        data-state={state}
        data-has-leading={leadingIcon ? 'true' : undefined}
        data-has-trailing={trailingNode ? 'true' : undefined}
        data-locked={locked ? 'true' : undefined}
      >
        {leadingIcon && <span className="lp-field-leading" aria-hidden="true">{leadingIcon}</span>}
        <Input
          ref={ref}
          id={id}
          variant="inline"
          className="lp-field-input"
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          readOnly={locked || inputProps.readOnly}
          {...inputProps}
        />
        {trailingNode && <span className="lp-field-trailing">{trailingNode}</span>}
      </div>
      {message}
    </div>
  )
})
